import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface Formateur {
  id: number;
  nom: string;
  prenoms: string;
  nom_complet: string;
  email: string;
  telephone: string;
  specialite: string;
  actif: boolean;
}

export interface FormateurCreate {
  nom: string;
  prenoms?: string;
  email?: string;
  telephone?: string;
  specialite?: string;
  actif?: boolean;
}

export interface FormateurMatiere {
  id: number;
  matiere: number;
  matiere_nom: string;
  matiere_code: string;
  matiere_niveau: string;
  matiere_semestre: number;
  matiere_option: string | null;
  matiere_classe: string;
  enseignant: number;
  annee_academique: string;
}

export interface AssignerMatieresRequest {
  matiere_ids: number[];
  annee_academique: string;
}

export interface AssignerMatieresConflict {
  matiere_id: number;
  matiere_nom: string;
  formateur_nom: string;
}

export interface AssignerMatieresResponse {
  success: boolean;
  ajoutees: number;
  retirees: number;
  total: number;
  conflits?: AssignerMatieresConflict[];
}

export interface PropagationSuggestion {
  matiere_nom: string;
  matiere_id_cible: number;
  option_cible: string;
  formateur_id: number;
  formateur_nom: string;
  option_source: string;
  matiere_id_source?: number;
}

export interface OptionsCommunes {
  type: 'options_identiques';
  niveau: string;
  option_a: string;
  option_b: string;
  taux_commun: number;
  nb_communes: number;
  nb_total: number;
  propagations: PropagationSuggestion[];
}

export interface MemeMatiereSuggestion {
  type: 'meme_matiere';
  niveau: string;
  matiere_nom: string;
  matiere_id_cible: number;
  option_cible: string;
  formateur_id: number;
  formateur_nom: string;
  option_source: string;
}

export interface FormateurMulti {
  formateur_id: number;
  formateur_nom: string;
  matiere_nom: string;
  options: string[];
  niveau: string;
}

export interface SuggestionsResponse {
  options_communes: OptionsCommunes[];
  propagations: MemeMatiereSuggestion[];
  formateurs_multi_options: FormateurMulti[];
  stats: {
    total_suggestions_propagation: number;
    total_options_communes: number;
    total_formateurs_multi: number;
  };
}

export interface AppliquerSuggestionsResponse {
  success: boolean;
  crees: number;
  ignores: number;
  conflits: Array<{ matiere_id: number; formateur_actuel: string }>;
}

export interface AttributionsAnnee {
  [matiere_id: string]: {
    formateur_id: number;
    formateur_nom: string;
  };
}

export const formateursApi = createApi({
  reducerPath: 'formateursApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Formateurs', 'FormateurMatieres', 'AttributionsAnnee'],
  endpoints: (builder) => ({
    getFormateurs: builder.query<Formateur[], Record<string, any> | void>({
      query: (params) => ({
        url: '/enseignants/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && response.results) return response.results;
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Formateurs'],
    }),

    getFormateurById: builder.query<Formateur, number>({
      query: (id) => `/enseignants/${id}/`,
      providesTags: ['Formateurs'],
    }),

    createFormateur: builder.mutation<Formateur, FormateurCreate>({
      query: (data) => ({
        url: '/enseignants/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Formateurs'],
    }),

    updateFormateur: builder.mutation<Formateur, { id: number; data: Partial<FormateurCreate> }>({
      query: ({ id, data }) => ({
        url: `/enseignants/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Formateurs'],
    }),

    deleteFormateur: builder.mutation<void, number>({
      query: (id) => ({
        url: `/enseignants/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Formateurs'],
    }),

    getFormateurMatieres: builder.query<FormateurMatiere[], { id: number; annee_academique?: string }>({
      query: ({ id, annee_academique }) => ({
        url: `/enseignants/${id}/matieres/`,
        params: annee_academique ? { annee_academique } : undefined,
      }),
      providesTags: ['FormateurMatieres'],
    }),

    assignerMatieres: builder.mutation<AssignerMatieresResponse, { id: number; data: AssignerMatieresRequest }>({
      query: ({ id, data }) => ({
        url: `/enseignants/${id}/assigner_matieres/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FormateurMatieres', 'AttributionsAnnee'],
    }),

    attribuerMatiere: builder.mutation<{
      success: boolean;
      message: string;
      conflit?: boolean;
      formateur_nom?: string;
      deja_attribuee?: boolean;
      remplacement?: boolean;
      ancien_formateur_nom?: string | null;
    }, { formateur_id: number; matiere_id: number; annee_academique: string; force?: boolean }>({
      query: (data) => ({
        url: '/enseignants/attribuer_matiere/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FormateurMatieres', 'AttributionsAnnee'],
    }),

    getAttributionsAnnee: builder.query<AttributionsAnnee, { annee_academique: string }>({
      query: ({ annee_academique }) => ({
        url: '/enseignants/attributions_annee/',
        params: { annee_academique },
      }),
      providesTags: ['AttributionsAnnee'],
    }),

    getSuggestionsAffectation: builder.query<SuggestionsResponse, { annee_academique: string; niveau?: string }>({
      query: (params) => ({
        url: '/enseignants/suggestions_affectation/',
        params,
      }),
      providesTags: ['AttributionsAnnee'],
    }),

    appliquerSuggestions: builder.mutation<AppliquerSuggestionsResponse, {
      annee_academique: string;
      attributions: Array<{ matiere_id: number; formateur_id: number }>;
    }>({
      query: (data) => ({
        url: '/enseignants/appliquer_suggestions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FormateurMatieres', 'AttributionsAnnee'],
    }),

    // Détection paires de classes similaires (≥90% matières en commun)
    getPairesSimilaires: builder.query<{
      seuil: number;
      annee_academique: string;
      paires: Array<{
        niveau: string;
        source_option: string;
        cible_option: string;
        nb_matieres_source: number;
        nb_matieres_cible: number;
        nb_communes: number;
        taux: number;
        nb_attributions_source: number;
        nb_attributions_cible: number;
        nb_copiables: number;
      }>;
    }, { seuil?: number; annee_academique: string }>({
      query: (params) => ({
        url: '/matieres-enseignants/paires-similaires/',
        params,
      }),
      providesTags: ['AttributionsAnnee'],
    }),

    // Copie d'attributions entre 2 classes similaires
    copierAttributions: builder.mutation<{
      success: boolean;
      nb_copiees: number;
      nb_deja_present: number;
      nb_sans_equivalent: number;
      message: string;
    }, {
      niveau: string;
      source_option: string;
      cible_option: string;
      annee_academique: string;
    }>({
      query: (data) => ({
        url: '/matieres-enseignants/copier-attributions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AttributionsAnnee', 'FormateurMatieres'],
    }),
  }),
});

export const {
  useGetFormateursQuery,
  useGetFormateurByIdQuery,
  useCreateFormateurMutation,
  useUpdateFormateurMutation,
  useDeleteFormateurMutation,
  useGetFormateurMatieresQuery,
  useAssignerMatieresMutation,
  useAttribuerMatiereMutation,
  useGetAttributionsAnneeQuery,
  useGetSuggestionsAffectationQuery,
  useAppliquerSuggestionsMutation,
  useGetPairesSimilairesQuery,
  useCopierAttributionsMutation,
} = formateursApi;
